import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetProjectStatsByIdParams,
  GetProjectStatsByIdData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetProjectStatsByIdState
  extends MergeRequestState<GetProjectStatsByIdData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getProjectStatsById,
  isFetching: (state: ReduxState) =>
    state.server.getProjectStatsById.isFetching,
  data: (state: ReduxState) => state.server.getProjectStatsById.data,
  error: (state: ReduxState) => state.server.getProjectStatsById.error,
};

const initialState: GetProjectStatsByIdState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getProjectStatsById';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      config,
    }: { params: GetProjectStatsByIdParams; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getProjectStatsById(params, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetProjectStatsById {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getProjectStatsById: ServerGetProjectStatsById = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
